import React, { MouseEventHandler, useEffect, useReducer, useRef } from 'react';
import produce from 'immer';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { Link, RouteComponentProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Banner, { Variant as BannerVariant } from '@leafygreen-ui/banner';
import { H2 } from '@leafygreen-ui/typography';

// styles
import { mq } from 'js/common/utils/mediaQueries';
import { Container, FooterText, header, submitButton } from './styles/form';
import { bannerStyle } from './styles/banner';

// analytics
import analytics, { PAGE_CALLS } from 'js/project/common/util/segmentAnalytics';
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';
// api
import * as api from 'js/common/services/api';

// utils
import * as oktaUtils from 'js/common/utils/oktaUtils';
import colors from 'js/auth/utils/palette';

// context
import { useRequestParams } from 'js/common/context/RequestParamsContext';

// types
import { Search } from 'js/auth/types/search';
import { Document } from 'js/common/types/browser';
import { FieldAction, FieldNames, FieldPayload } from 'js/auth/types/field';
import { onChangeTypeDef } from 'js/auth/types/formInput';

// services
import { justLoggedInRedirect } from 'js/auth/utils/authRedirectUtils';
import * as errorHelper from 'js/common/services/authErrorHelper';
import { getMarketplaceRegistrationCookie } from 'js/common/utils/billingHelpers';

// hooks
import { useQuoraTrackingPixel } from 'js/auth/hooks/useQuoraTrackingPixel';

// components
import ButtonWithLoaderAndLink from 'js/common/components/ButtonWithLoaderAndLink';
import LineDivider from 'js/common/components/LineDivider';
import Layout from './Layout';
import FormInput from './FormInput';
import HeaderLogo from './HeaderLogo';
import InviteBanner from './InviteBanner';
import ReCaptcha, { ReCaptchaHandles } from './ReCaptcha';
import GoogleAuthButton from './GoogleAuthButton';
import VercelLoginForm from './VercelLoginForm';
import AuthErrorBanner from './AuthErrorBanner';
import SocialAuthButton, { SocialAuthProvider } from './SocialAuthButton';

const forgotPassword = css({
  fontSize: '14px',
  textDecoration: 'none',
  color: colors.link,
  cursor: 'pointer',
});

const marketplaceLink = css({
  color: colors.link,
});

const ChangeEmail = styled.div((props: { hasError?: boolean }) =>
  mq({
    position: 'relative',
    bottom: ['50px', '58px', '50px', '50px'],
    left: props.hasError
      ? ['215px', 'calc(100vw - 119px)', '215px', '215px']
      : ['239px', 'calc(100vw - 91px)', '239px', '239px'],
    display: 'inline',
  })
);

const formInputComponentWidth = ['300px', 'calc(100vw - 32px)', '300px', '300px'];

const LinkContainer = styled.div(() =>
  mq({
    display: 'inline-block',
    textAlign: 'right',
    marginTop: '20px',
    width: formInputComponentWidth,
  })
);

const StyledLineDivider = styled(LineDivider)(
  mq({
    width: formInputComponentWidth,
    margin: '20px 0',
  })
);

// TODO: Uncomment this as part of https://jira.mongodb.org/browse/CLOUDP-57726
// const Iframe = styled.iframe`
//   width: 0;
//   height: 0;
//   border: 0;
// `;

interface LoginPageProps {
  location?: Pick<Location, 'search'>;
  oktaSessionExists?: () => Promise<boolean>;
  endOktaSession?: () => void;
  windowLocation?: Pick<Location, 'assign' | 'href'>;
  browserDocument?: Document;
  match?: RouteComponentProps;
}

interface AuthResponse {
  uId?: string;
  username?: string;
  email?: string;
  currentOrgId?: string;
  csrfTime?: string;
  csrfToken?: string;
  loginRedirect: string;
  status: string;
}

interface ChangeUsername {
  type: 'changeUsername';
}

interface ProcessIdpResponse {
  type: 'processIdpResponse';
  payload: {
    onlyUsernameShowing: boolean;
    errorCode: string;
    isSubmitDisabled: boolean;
  };
}

interface ProcessAuthErrorResponse {
  type: 'processAuthErrorResponse';
  payload: {
    errorCode: string;
    formSubmitting: boolean;
    bannerVariant?: BannerVariant;
    marketingUrl?: string;
  };
}

interface SetShowSupportLogout {
  type: 'setShowSupportLogout';
  payload?: true;
}

interface SetFormSubmitting {
  type: 'setFormSubmitting';
  payload: boolean;
}

interface IsVercelIntegration {
  type: 'isVercelIntegration';
  payload: string;
}

interface SetReCaptchaResponse {
  type: 'setReCaptchaResponse';
  payload: {
    reCaptchaResponse: string;
  };
}

interface BypassSso {
  type: 'bypassSso';
}

interface ReducerState {
  username: string;
  password: string;
  onlyUsernameShowing: boolean;
  formSubmitting: boolean;
  // showSupportLogout: boolean; // TODO: AM CLOUDP-57726 uncomment and fix the support logout issue
  authErrorCode: string;
  reCaptchaResponse: null | string;
  bannerVariant?: BannerVariant;
  ssoBypassEnabled: boolean;
  differentUniversityPasswordError: boolean;
  marketingUrl?: string;
  isSubmitDisabled: boolean;
  isLoginDisabled: boolean;
}

const reducerState: ReducerState = {
  username: '',
  password: '',
  onlyUsernameShowing: true,
  formSubmitting: false,
  // showSupportLogout: false, // TODO: AM CLOUDP-57726 uncomment and fix the support logout issue
  authErrorCode: '',
  bannerVariant: BannerVariant.Danger,
  reCaptchaResponse: null,
  ssoBypassEnabled: false,
  differentUniversityPasswordError: false,
  marketingUrl: '',
  isSubmitDisabled: true,
  isLoginDisabled: true,
};

const loginReducer = produce(
  (
    draft: ReducerState,
    action:
      | FieldAction
      | SetFormSubmitting
      | ProcessIdpResponse
      | SetShowSupportLogout
      | ProcessAuthErrorResponse
      | SetReCaptchaResponse
      | BypassSso
      | ChangeUsername
      | IsVercelIntegration
  ) => {
    switch (action.type) {
      case 'field': {
        const { field, value } = action.payload as FieldPayload;
        draft[field] = value;
        return;
      }
      case 'setFormSubmitting': {
        draft.formSubmitting = action.payload;
        return;
      }
      case 'processIdpResponse': {
        const { onlyUsernameShowing, errorCode, isSubmitDisabled } = action.payload;
        draft.onlyUsernameShowing = onlyUsernameShowing;
        draft.authErrorCode = errorCode;
        draft.isSubmitDisabled = isSubmitDisabled;
        return;
      }
      // TODO: AM CLOUDP-57726 uncomment and fix the support logout issue
      // case 'setShowSupportLogout': {
      //   draft.showSupportLogout = true;
      //   return;
      // }
      case 'processAuthErrorResponse': {
        const { errorCode, formSubmitting, bannerVariant, marketingUrl } = action.payload;
        const isUniversityPasswordError = errorCode === 'DIFFERENT_UNIVERSITY_PASSWORD';
        const isSubmitDisabled = errorCode === 'INVALID_USER_AUTH' || draft.onlyUsernameShowing;

        if (isUniversityPasswordError) {
          draft.differentUniversityPasswordError = true;
          return;
        }
        draft.marketingUrl = marketingUrl;
        draft.authErrorCode = errorCode;
        draft.bannerVariant = bannerVariant;
        draft.formSubmitting = formSubmitting;
        draft.isSubmitDisabled = isSubmitDisabled;
        return;
      }
      case 'setReCaptchaResponse': {
        const { reCaptchaResponse } = action.payload;

        draft.reCaptchaResponse = reCaptchaResponse;
        return;
      }
      case 'bypassSso': {
        draft.ssoBypassEnabled = true;
        draft.onlyUsernameShowing = false;
        return;
      }
      case 'changeUsername': {
        draft.onlyUsernameShowing = true;
        draft.username = '';
        draft.password = '';
        draft.authErrorCode = '';
        draft.isSubmitDisabled = false;
        return;
      }
      default:
        return draft;
    }
  },
  reducerState
);

export default function LoginPage({
  location: { search } = { search: '' },
  oktaSessionExists = oktaUtils.oktaSessionExists,
  endOktaSession = oktaUtils.endOktaSession,
  windowLocation = window.location,
  browserDocument = document,
  match: { params } = { params: '' },
}: LoginPageProps) {
  const {
    baasCentralUrl,
    centralUrl,
    universityCentralUrl,
    isLdap,
    userRegistrationEnabled,
    reCaptchaEnabledLogin,
    isGoogleAuthEnabled,
    isGithubAuthEnabled,
    onPrem,
    oktaSessionDiscoveryEnabled,
    userRegistrationDisabledRedirectUrl,
    atlasSiteFullName,
    siteFullName,
    vercelEmail,
    vercelUserId,
    vercelTeamName,
    vercelTeamId,
  } = useRequestParams();

  const [
    {
      username,
      password,
      onlyUsernameShowing,
      formSubmitting,
      // showSupportLogout,
      authErrorCode,
      reCaptchaResponse,
      bannerVariant,
      ssoBypassEnabled,
      differentUniversityPasswordError,
      marketingUrl,
      isSubmitDisabled,
      isLoginDisabled,
    },
    dispatch,
  ] = useReducer(loginReducer, {
    ...reducerState,
    onlyUsernameShowing: !onPrem,
  });

  const isSocialAuthEnabled = isGoogleAuthEnabled || isGithubAuthEnabled;
  const isVercelIntegration = params?.integration === 'vercel';
  const parsedSearchLocation = queryString.parse(search) as Search;
  const {
    activationCode,
    inviteToken,
    username: invitedUsername,
    socialUsername,
    unauthorizedUsername,
    reason,
    idp,
    ssoDebug,
  } = parsedSearchLocation;

  const clientState = { ...parsedSearchLocation };

  const reCaptchaRef = useRef<ReCaptchaHandles>(null);
  const marketplaceRegistrationCookie = getMarketplaceRegistrationCookie();
  const marketplaceType = marketplaceRegistrationCookie?.partnerType;

  useEffect(() => {
    // Segment page call viewing the login page
    analytics.page(PAGE_CALLS.SIGN_IN, {
      isFromMp: !!marketplaceRegistrationCookie,
      marketplaceSource: marketplaceRegistrationCookie?.partnerType ?? '',
    });
  }, []);

  const prefilledUsername = invitedUsername || socialUsername || unauthorizedUsername;

  useEffect(() => {
    if (prefilledUsername) {
      dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: prefilledUsername } });
    }
  }, [prefilledUsername, isVercelIntegration, vercelEmail]);

  useEffect(() => {
    if (reason) {
      const bannerVariant =
        reason === 'SOCIAL_USERNAME_DOMAIN_REQUIRES_FEDERATION'
          ? BannerVariant.Warning
          : reason === 'IDLE_SESSION_TIMEOUT' || reason === 'ACCOUNT_DELETION_REQUESTED'
          ? BannerVariant.Info
          : BannerVariant.Danger;
      dispatch({
        type: 'processAuthErrorResponse',
        payload: {
          errorCode: reason,
          bannerVariant,
          formSubmitting: false,
          marketingUrl: userRegistrationDisabledRedirectUrl,
        },
      });
    }
  }, [reason]);

  useEffect(() => {
    let disableSubmit = true;
    if ((onlyUsernameShowing && username) || password) {
      disableSubmit = false;
    }
    dispatch({ type: 'field', payload: { field: FieldNames.IS_SUBMIT_DISABLED, value: disableSubmit } });
  }, [password, username, onlyUsernameShowing]);

  useQuoraTrackingPixel();

  useEffect(() => {
    async function checkIfSessionExists() {
      const parsedLocation = queryString.parse(search) as Search;
      const { signedOut, reason } = parsedLocation;
      const hasError = !!authErrorCode || !!reason;
      const shouldEndSessions = signedOut === 'true' || !oktaSessionDiscoveryEnabled || !!inviteToken || hasError;
      try {
        const sessionExists = await oktaSessionExists();
        // regardless of an existing okta session, app sessions must be terminated, so this flag takes precedence
        // over `sessionExists`
        if (shouldEndSessions) {
          dispatch({ type: 'field', payload: { field: FieldNames.IS_LOGIN_DISABLED, value: true } });
          const promiseArray = [
            api.auth.accountLogout(),
            api.auth.cloudLogout(centralUrl),
            api.auth.universityLogout(universityCentralUrl),
          ];
          // TODO: AM CLOUDP-57726 uncomment and fix the support logout issue
          // dispatch({ type: 'setShowSupportLogout' });
          if (sessionExists) {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'void' is not assignable to param... Remove this comment to see the full error message
            promiseArray.push(endOktaSession());
          }

          await Promise.all(promiseArray);
          dispatch({ type: 'field', payload: { field: FieldNames.IS_LOGIN_DISABLED, value: false } });
        } else if (sessionExists) {
          // Log the user into the correct application, knowing they already have an Okta session
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ clientState: { inviteToken?: s... Remove this comment to see the full error message
          const { loginRedirect } = await api.auth.startOidc({
            clientState: {
              ...parsedLocation,
              ...(isVercelIntegration && {
                n: '/account/vercel/org/selection',
                isVercelIntegration: true,
                vercelEmail,
                vercelUserId,
                vercelTeamId,
                vercelTeamName,
              }),
            },
          });
          windowLocation.assign(loginRedirect);
        } else {
          dispatch({ type: 'field', payload: { field: FieldNames.IS_LOGIN_DISABLED, value: false } });
        }
      } catch (error) {
        console.log('Some error happened for ending the sessions', error);
        sendError({
          error,
          team: CloudTeams.CoreIam,
        });
        dispatch({ type: 'field', payload: { field: FieldNames.IS_LOGIN_DISABLED, value: false } });
      } finally {
        if (shouldEndSessions) {
          try {
            analytics.reset();
          } catch (error) {
            sendError({
              error,
              team: CloudTeams.AtlasGrowth,
            });
          }
        }
      }
    }

    if (!onPrem) {
      checkIfSessionExists();
    } else {
      dispatch({ type: 'field', payload: { field: FieldNames.IS_LOGIN_DISABLED, value: false } });
    }
  }, [authErrorCode, onPrem, search]);

  useEffect(() => {
    if (reCaptchaResponse != null) {
      callAuthApi();
    }
  }, [reCaptchaResponse]);

  useEffect(() => {
    if (differentUniversityPasswordError) {
      windowLocation.assign(`/account/reset/university/password?email=${encodeURIComponent(username)}`);
    }
  }, [differentUniversityPasswordError]);

  useEffect(() => {
    if (ssoDebug) {
      dispatch({ type: 'bypassSso' });
    }
  }, [ssoDebug]);

  const callAuthApi = async () => {
    const loginData = {
      username,
      password,
      inviteToken,
      clientState: {
        ...clientState,
        ...(isVercelIntegration && {
          n: '/account/vercel/org/selection',
          isVercelIntegration: true,
          vercelEmail,
          vercelUserId,
          vercelTeamId,
          vercelTeamName,
        }),
      },
      reCaptchaResponse,
      ssoDebug,
      idp,
    };
    let authResponse: AuthResponse;

    try {
      authResponse = onPrem ? await api.user.auth(loginData) : await api.auth.userAuthOkta(loginData);
    } catch ({ errorCode, resource }) {
      dispatch({ type: 'processAuthErrorResponse', payload: { errorCode, formSubmitting: false } });

      if (reCaptchaEnabledLogin && reCaptchaRef.current) {
        reCaptchaRef.current.reset();
      }

      if (errorHelper.hasMatchingErrorCode(errorCode) && resource) {
        windowLocation.assign(`${resource}?reason=${errorCode}`);
      }
      return;
    }

    const { uId: userId, username: responseUsername, email, loginRedirect } = authResponse;

    if (onPrem) {
      justLoggedInRedirect({
        userId,
        email,
        baasCentralUrl,
        username: responseUsername,
        windowLocation,
        query: parsedSearchLocation,
      });
    } else {
      windowLocation.assign(loginRedirect);
    }
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    dispatch({ type: 'setFormSubmitting', payload: true });
    const hasValidRememberDeviceCookie =
      browserDocument.cookie && browserDocument.cookie.includes(`remember-user-device=${username.toLowerCase()}:`);

    if (onlyUsernameShowing) {
      try {
        const { loginRedirect } = await api.federation.getIdentityProviderRedirect({
          username: username!,
          clientState: {
            ...clientState,
            ...(isVercelIntegration && {
              n: '/account/vercel/org/selection',
              isVercelIntegration: true,
              vercelEmail,
              vercelUserId,
              vercelTeamId,
              vercelTeamName,
            }),
          },
        });

        if (loginRedirect) {
          windowLocation.assign(loginRedirect);
        }

        dispatch({
          type: 'processIdpResponse',
          payload: {
            onlyUsernameShowing: !onPrem && !!loginRedirect,
            errorCode: '',
            isSubmitDisabled: false,
          },
        });
      } catch (err) {
        const { errorCode } = err;
        dispatch({
          type: 'processIdpResponse',
          payload: {
            onlyUsernameShowing: true,
            errorCode,
            isSubmitDisabled: false,
          },
        });
      } finally {
        dispatch({ type: 'setFormSubmitting', payload: false });
      }
    } else if (reCaptchaEnabledLogin && reCaptchaRef.current && !hasValidRememberDeviceCookie) {
      reCaptchaRef.current.execute();
    } else {
      callAuthApi();
    }
  };

  const onEmailChange: onChangeTypeDef = (e) => {
    dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: e.target.value } });
  };

  const onPasswordChange: onChangeTypeDef = (e) => {
    dispatch({ type: 'field', payload: { field: FieldNames.PASSWORD, value: e.target.value } });
  };

  const onChangeEmailClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    dispatch({ type: 'changeUsername' });
  };

  const usernameOrEmailTooltip = onPrem ? 'This is your username or email address you used when you registered.' : '';
  const usernameOrEmail = onPrem ? 'Username' : 'Email Address';

  const alertableErrorCodes = [
    'SOCIAL_USERNAME_DOMAIN_REQUIRES_FEDERATION',
    'USER_ORGS_RESTRICTED',
    'SSO_DEBUG_PROHIBITED',
    'IDLE_SESSION_TIMEOUT',
    'ACCOUNT_DELETION_REQUESTED',
    'JIT_FAILURE',
  ];
  const displayErrorAsAlert = !!authErrorCode && alertableErrorCodes.includes(authErrorCode);

  const prefilledUsernameChanged = prefilledUsername && username !== prefilledUsername;
  const showInputErrors =
    !!authErrorCode &&
    !alertableErrorCodes.includes(authErrorCode) &&
    bannerVariant === BannerVariant.Danger &&
    !prefilledUsernameChanged;

  const disableEmailInput = !onlyUsernameShowing && !ssoBypassEnabled && !onPrem;

  if (isVercelIntegration) {
    return (
      <VercelLoginForm
        {...{
          search,
          email: username,
          password,
          emailTooltipText: usernameOrEmailTooltip,
          authErrorCode,
          marketingUrl,
          siteName: atlasSiteFullName,
          showInputErrors,
          showPasswordInput: !onlyUsernameShowing,
          disableEmailInput,
          onlyUsernameShowing,
          isFormSubmitting: formSubmitting,
          isSubmitDisabled: isSubmitDisabled || isLoginDisabled,
          isLoginDisabled,
          isGoogleAuthEnabled,
          clientState: {
            n: '/account/vercel/org/selection',
            isVercelIntegration: true,
            vercelEmail,
            vercelUserId,
            vercelTeamId,
            vercelTeamName,
          },
          onEmailChange,
          onPasswordChange,
          onChangeEmailClick,
          onSubmit: onFormSubmit,
        }}
      />
    );
  }
  return (
    <>
      <Helmet title="Log in" />
      <Layout contentPlacement="left" isLoginPromo>
        <Container onSubmit={onFormSubmit} method="post">
          <HeaderLogo />
          <InviteBanner
            token={inviteToken}
            username={invitedUsername}
            isFor="login"
            retryInviteCheck={formSubmitting}
          />
          {activationCode && (
            <Banner variant={BannerVariant.Danger} css={bannerStyle} data-testid="bannerActivationCode">
              You&apos;re about to redeem your activation code. It will automatically be applied to your new
              organization.
            </Banner>
          )}
          {marketplaceRegistrationCookie && (
            <Banner variant={BannerVariant.Info} css={bannerStyle} data-testid="bannerMpRegistration">
              Don&apos;t have a MongoDB account yet?{' '}
              <Link to="/register" css={marketplaceLink}>
                Sign up
              </Link>{' '}
              to complete linking your {marketplaceType} account to MongoDB.
            </Banner>
          )}
          <H2 data-testid="login-page-header" css={header}>
            Log in to your account
          </H2>
          {displayErrorAsAlert && (
            <Banner variant={bannerVariant || BannerVariant.Danger} data-testid="bannerLoginError" css={bannerStyle}>
              {errorHelper.getErrorMessageFromCode(authErrorCode)}
            </Banner>
          )}
          {isGoogleAuthEnabled && (
            <>
              <GoogleAuthButton
                disabled={isLoginDisabled}
                displayText="Log in with Google"
                location={location}
                marketplaceLinkFlow={marketplaceType}
              />
            </>
          )}
          {isGithubAuthEnabled && (
            <>
              <SocialAuthButton
                disabled={isLoginDisabled}
                socialAuthProvider={SocialAuthProvider.GITHUB}
                location={location}
                marketplaceLinkFlow={marketplaceType}
              />
            </>
          )}
          {isSocialAuthEnabled && <StyledLineDivider>or</StyledLineDivider>}

          {authErrorCode && !displayErrorAsAlert && (
            <AuthErrorBanner
              css={bannerStyle}
              authErrorCode={authErrorCode}
              marketingUrl={marketingUrl}
              siteName={onPrem ? siteFullName : atlasSiteFullName}
              onPrem={onPrem}
            />
          )}

          <FormInput
            fieldName="username"
            labelName={usernameOrEmail}
            autoComplete="email"
            onChange={onEmailChange}
            value={username}
            hasError={showInputErrors}
            showChangeUsername={!onlyUsernameShowing}
            disabled={disableEmailInput || !!invitedUsername}
            tooltipText={usernameOrEmailTooltip}
            autoFocus
          />
          {disableEmailInput && (
            <ChangeEmail hasError={showInputErrors}>
              <a tabIndex={-1} role="button" onClick={onChangeEmailClick} css={forgotPassword}>
                Change
              </a>
            </ChangeEmail>
          )}
          {!onlyUsernameShowing && (
            <>
              <FormInput
                fieldName="password"
                labelName="Password"
                autoComplete="current-password"
                type="password"
                hasError={showInputErrors}
                onChange={onPasswordChange}
                value={password}
                autoFocus={!ssoBypassEnabled && !onPrem}
              />
              {!isLdap && (
                <LinkContainer>
                  <Link
                    to={{
                      pathname: '/reset/password',
                      search: `?email=${encodeURIComponent(username)}`,
                    }}
                    css={forgotPassword}
                  >
                    &nbsp;Forgot Password?
                  </Link>
                </LinkContainer>
              )}
            </>
          )}
          {reCaptchaEnabledLogin && (
            <ReCaptcha
              invisible
              callback={(response) =>
                dispatch({ type: 'setReCaptchaResponse', payload: { reCaptchaResponse: response } })
              }
              ref={reCaptchaRef}
            />
          )}
          <footer>
            <ButtonWithLoaderAndLink
              variant="primary"
              css={submitButton}
              type="submit"
              showLoader={formSubmitting}
              disabled={isSubmitDisabled || isLoginDisabled}
              name="login"
            >
              {onlyUsernameShowing ? 'Next' : 'Login'}
            </ButtonWithLoaderAndLink>
            {userRegistrationEnabled && (
              <FooterText>
                Don&apos;t have an account?
                <Link to={!onPrem ? `/register${search}` : '/register'}>&nbsp;Sign Up</Link>
              </FooterText>
            )}
          </footer>
          {/* TODO: Uncomment this as part of https://jira.mongodb.org/browse/CLOUDP-57726
           {showSupportLogout && (
            <Iframe title="&nbsp;" tabIndex={-1} src="https://mongodbcommunity.force.com/secur/logout.jsp" />
          )} */}
        </Container>
      </Layout>
    </>
  );
}
